'use client';

import './home-overview.scss';

import type { TFunction } from 'i18next';
import type { PropsWithChildren } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { LinkCardSkeleton } from '@sola/ark-core/molecules/link-card/link-card-skeleton.tsx';
import {
  LinkCard,
  LinkCardIcon,
} from '@sola/ark-core/molecules/link-card/link-card.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { getChildBioPath } from '@sola/my-sdk/constants.ts';
import { GraphQLCommitmentType } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { getAgeFromBirthday } from '@sola/shared-utils/children/utils.ts';

import { IconTile } from '../../atoms/icon-tile/icon-tile.tsx';
import { PolaroidImage } from '../../atoms/polaroid-image/polaroid-image.tsx';
import beyondMeasureLogo from '../../images/pledge-beyond-measure.png';
import { CommitmentDetailsSheet } from '../../organisms/commitments-details-sheet/commitment-details-sheet.tsx';
import { useNoSponsorshipsCta } from '../../utils/contentful.ts';
import type { PrimaryCtaResult } from '../../utils/home/primary-cta-decision.ts';
import { PrimaryCtaType } from '../../utils/home/primary-cta-decision.ts';
import { lastWrittenFallbackKey } from '../../utils/home/secondary-cta-decision.ts';
import type { SecondaryCtaResult } from '../../utils/home/secondary-cta-decision.ts';
import { formatPledgeName } from '../../utils/pledges.ts';
import { AffinityCardPledge } from '../affinity-card-pledge/affinity-card-pledge.tsx';
import { AffinityCardSponsorship } from '../affinity-card-sponsorship/affinity-card-sponsorship.tsx';
import { AffinityCardSkeleton } from '../affinity-card/affinity-card.tsx';
import { PrimaryCtaBirthday } from '../primary-cta-birthday/primary-cta-birthday.tsx';
import { PrimaryCtaChristmas } from '../primary-cta-christmas/primary-cta-christmas.tsx';
import { PrimaryCtaDeparture } from '../primary-cta-departure/primary-cta-departure.tsx';
import { PrimaryCtaLetterRead } from '../primary-cta-letter-read/primary-cta-letter-read.tsx';
import { PrimaryCtaNoSponsorships } from '../primary-cta-no-sponsorships/primary-cta-no-sponsorships.tsx';
import type {
  HomeOverviewCommitmentItem,
  HomeOverviewData,
  HomeOverviewItem,
  HomeOverviewPledgeItem,
} from './home-overview.logic.ts';
import {
  HomeOverviewType,
  HomeOverviewVariant,
  useHomeOverview,
} from './home-overview.logic.ts';

const renderListItem = (item: HomeOverviewItem): React.ReactNode => {
  switch (item.type) {
    case HomeOverviewType.Commitment: {
      return (
        <AffinityCardSponsorship
          key={item.commitment.globalId}
          commitment={item.commitment}
        />
      );
    }
    case HomeOverviewType.Pledge: {
      return (
        <AffinityCardPledge key={item.pledge.pledgeId} pledge={item.pledge} />
      );
    }
  }
};

const formatAge = (
  t: TFunction,
  birthdate?: string | null,
): string | undefined => {
  if (!birthdate) {
    return undefined;
  }

  const age = getAgeFromBirthday(birthdate);
  if (typeof age !== 'number') {
    return undefined;
  }

  return t('my:homepage.overview.gallery.age', { count: age });
};

const HomeOverviewCommitmentGalleryTile: React.FC<{
  item: HomeOverviewCommitmentItem;
}> = ({ item }) => {
  const { t } = useTranslation(['my']);
  const participant = item.participant;
  const title = participant?.name ?? '';
  const metadata = formatAge(t, participant?.birthdate);
  const ariaLabel = metadata ? `${title} ${metadata}` : title;
  const isCorrespondent =
    item.commitment.commitmentType === GraphQLCommitmentType.Correspondence;

  return (
    // TODO: Link to child details page
    <Link
      href={getChildBioPath(participant?.globalId)}
      className="HomeOverview__gallery-tile flex w-full flex-col justify-between overflow-clip px-comfy-sm py-comfy-lg rounded-md label-neutral-amplified text-center no-underline"
      aria-label={ariaLabel}
    >
      <div className="HomeOverview__image-wrap relative flex items-start justify-center">
        {participant?.photoUrl && (
          <div className="relative inline-flex">
            {isCorrespondent && (
              <div className="HomeOverview__badge-wrap absolute">
                <Badge
                  label={t('my:sponsorshipRelationship.correspondent')}
                  iconBefore="mail-04"
                  size="sm"
                />
              </div>
            )}
            <PolaroidImage
              size="sm"
              tilt="randomized"
              childImageUrl={participant.photoUrl}
              alt={title}
            />
          </div>
        )}
      </div>

      <div className="HomeOverview__gallery-tile-text flex w-full flex-col gap-snug-xl text-center">
        <span className="HomeOverview__gallery-tile-title overflow-hidden label-potent label-md label-neutral-amplified">
          {title}
        </span>
        {metadata && (
          <span className="label-moderate label-sm label-neutral-mild">
            {metadata}
          </span>
        )}
      </div>
    </Link>
  );
};

const HomeOverviewPledgeGalleryTile: React.FC<{
  item: HomeOverviewPledgeItem;
}> = ({ item }) => {
  const { t } = useTranslation(['my']);
  const [isOpen, setIsOpen] = useState(false);
  const title = formatPledgeName(item.pledge.pledgeName, t);

  return (
    // TODO: I thought we'd take them to the billing/commitments page and open the sheet there...
    <CommitmentDetailsSheet
      isOpen={isOpen}
      onOpenChange={(open) => setIsOpen(open)}
      pledge={item.pledge}
      openTrigger={
        <button
          type="button"
          className="HomeOverview__gallery-tile flex w-full flex-col justify-between overflow-clip px-comfy-sm py-comfy-lg rounded-md label-neutral-amplified text-center no-underline"
        >
          <div className="HomeOverview__image-wrap relative flex items-start justify-center">
            <IconTile imgSrc={beyondMeasureLogo} alt={title} size="sm" />
          </div>

          <div className="HomeOverview__gallery-tile-text flex w-full flex-col gap-snug-xl text-center">
            <span className="HomeOverview__gallery-tile-title overflow-hidden label-potent label-md label-neutral-amplified">
              {title}
            </span>
            <span className="label-moderate label-sm label-neutral-mild">
              {t('my:homepage.affinityCard.pledge.subtitle')}
            </span>
          </div>
        </button>
      }
    />
  );
};

const HomeOverviewGalleryItem: React.FC<{ item: HomeOverviewItem }> = ({
  item,
}) => {
  switch (item.type) {
    case HomeOverviewType.Commitment: {
      return <HomeOverviewCommitmentGalleryTile item={item} />;
    }
    case HomeOverviewType.Pledge: {
      return <HomeOverviewPledgeGalleryTile item={item} />;
    }
  }
};

const getGalleryItemKey = (item: HomeOverviewItem): string => {
  switch (item.type) {
    case HomeOverviewType.Commitment: {
      return item.commitment.globalId;
    }
    case HomeOverviewType.Pledge: {
      return item.pledge.pledgeId;
    }
  }
};

const HomeOverviewMoreGalleryTile: React.FC<{
  href: string;
}> = ({ href }) => {
  const { t } = useTranslation(['my']);

  return (
    <Link
      href={href}
      className="HomeOverview__gallery-tile HomeOverview__gallery-tile--more flex w-full flex-col justify-between overflow-clip px-comfy-sm py-comfy-lg rounded-md label-neutral-amplified text-center no-underline"
      aria-label={t('my:homepage.overview.gallery.more')}
    >
      <div className="HomeOverview__image-wrap relative flex items-start justify-center">
        <div className="HomeOverview__more-icon flex items-center justify-center rounded-full label-neutral-amplified">
          <ArkIcon iconName="maximize-01" size="24" />
        </div>
      </div>

      <div className="HomeOverview__gallery-tile-text flex w-full flex-col gap-snug-xl text-center">
        <span className="HomeOverview__gallery-tile-title overflow-hidden label-potent label-md label-neutral-amplified">
          {t('my:homepage.overview.gallery.more')}
        </span>
      </div>
    </Link>
  );
};

const HomeOverviewGallery: React.FC<{
  items: HomeOverviewItem[];
  showMoreTile: boolean;
  moreHref: string | null;
}> = ({ items, showMoreTile, moreHref }) => {
  const { t } = useTranslation(['my']);

  return (
    <div
      className="HomeOverview__carousel @container overflow-x-auto"
      aria-label={t('my:homepage.overview.gallery.label')}
      role="region"
    >
      <div className="HomeOverview__gallery-track flex items-start">
        {items.map((item) => (
          <div
            key={getGalleryItemKey(item)}
            className="HomeOverview__gallery-slide"
          >
            <HomeOverviewGalleryItem item={item} />
          </div>
        ))}
        {showMoreTile && moreHref && (
          <div className="HomeOverview__gallery-slide">
            <HomeOverviewMoreGalleryTile href={moreHref} />
          </div>
        )}
      </div>
    </div>
  );
};

export const HomeOverviewPrimaryCta: React.FC<{ cta: PrimaryCtaResult }> = ({
  cta,
}) => {
  switch (cta.type) {
    case PrimaryCtaType.NoSponsorships: {
      return <PrimaryCtaNoSponsorships />;
    }
    case PrimaryCtaType.Departure: {
      return (
        <PrimaryCtaDeparture key="departure" participants={cta.participants} />
      );
    }
    case PrimaryCtaType.Birthday: {
      return (
        <PrimaryCtaBirthday
          key="birthday"
          participants={cta.participants}
          confirmed={cta.confirmed}
        />
      );
    }
    case PrimaryCtaType.Christmas: {
      return (
        <PrimaryCtaChristmas key="christmas" sponsorships={cta.sponsorships} />
      );
    }
    case PrimaryCtaType.UnreadLetters: {
      return <PrimaryCtaLetterRead key="read" letters={cta.letters} />;
    }
    case PrimaryCtaType.NewChildPhoto: {
      // TODO(CTA): Render once the new-child-photo component exists.
      return null;
    }
    case PrimaryCtaType.GeneralAppeal: {
      // TODO(CTA): Render once the general-appeal component exists.
      return null;
    }
  }
};

export const HomeOverviewSecondaryCta: React.FC<{
  cta: SecondaryCtaResult;
}> = ({ cta }) => {
  const { t } = useTranslation(['my', 'notifications']);

  // The engine stays pure and emits the fallback i18n key as the `lastWritten`
  // value when a sponsorship has no correspondence date. Resolve it to the
  // translated string ("N/A") here before interpolating.
  const interpolation =
    cta.interpolation?.lastWritten === lastWrittenFallbackKey
      ? { ...cta.interpolation, lastWritten: t(lastWrittenFallbackKey) }
      : cta.interpolation;
  const href = 'hrefType' in cta ? cta.url : t(cta.hrefKey);

  return (
    <LinkCard
      className="h-full"
      href={href}
      graphic={
        <LinkCardIcon>
          <ArkIcon iconName={cta.iconName} size="18" />
        </LinkCardIcon>
      }
      title={t(cta.titleKey, interpolation)}
      subtitle={cta.subtitleKey ? t(cta.subtitleKey, interpolation) : undefined}
      staticPadding
    />
  );
};

export const HomeOverviewSecondaryCtaWrapper: React.FC<PropsWithChildren> = ({
  children,
}) => {
  return (
    <div className="flex flex-col md:flex-row gap-comfy-sm w-full">
      {children}
    </div>
  );
};

export const HomeOverviewCtaSkeleton: React.FC = () => {
  return (
    <HomeOverviewSecondaryCtaWrapper>
      <div className="flex-1">
        <LinkCardSkeleton staticPadding />
      </div>
      <div className="flex-1">
        <LinkCardSkeleton staticPadding />
      </div>
    </HomeOverviewSecondaryCtaWrapper>
  );
};

const HomeOverviewCtas = ({
  showCtas,
  primaryCtas,
  secondaryCtas,
}: {
  showCtas: boolean;
  primaryCtas: HomeOverviewData['primaryCtas'];
  secondaryCtas: HomeOverviewData['secondaryCtas'];
}) => {
  if (!showCtas) {
    return <HomeOverviewCtaSkeleton />;
  }

  return (
    <>
      {primaryCtas.map((cta) => (
        <HomeOverviewPrimaryCta key={cta.type} cta={cta} />
      ))}
      {secondaryCtas.length > 0 && (
        <HomeOverviewSecondaryCtaWrapper>
          {secondaryCtas.map((cta) => (
            <div key={cta.type} className="flex-1">
              <HomeOverviewSecondaryCta cta={cta} />
            </div>
          ))}
        </HomeOverviewSecondaryCtaWrapper>
      )}
    </>
  );
};

export const HomeOverviewSkeleton: React.FC = () => {
  return (
    <div className="HomeOverview flex flex-col md:py-comfy-lg md:px-comfy-xs gap-comfy-sm rounded-lg">
      <AffinityCardSkeleton />
      <HomeOverviewSecondaryCtaWrapper>
        <div className="flex-1">
          <LinkCardSkeleton staticPadding />
        </div>
        <div className="flex-1">
          <LinkCardSkeleton staticPadding />
        </div>
      </HomeOverviewSecondaryCtaWrapper>
    </div>
  );
};

export const HomeOverviewLoadingErrorAlert: React.FC = () => {
  const { t } = useTranslation(['my']);
  return (
    <Alert
      variant="negative"
      title={t('my:homepage.queryError.title')}
      message={t('my:homepage.queryError.message')}
      action={
        <a href={t('my:ctas.contactSupport.url')}>
          {t('my:ctas.contactSupport.text')}
        </a>
      }
    />
  );
};

export const HomeOverview = () => {
  useTranslation(['core', 'geo', 'greetings', 'my', 'notifications']);

  const noSponsorshipsContent = useNoSponsorshipsCta();
  const {
    variant,
    items,
    showMoreTile,
    moreHref,
    showCtas,
    primaryCtas,
    secondaryCtas,
    isError,
  } = useHomeOverview(noSponsorshipsContent !== null);

  if (variant === HomeOverviewVariant.Loading) {
    return <HomeOverviewSkeleton />;
  }

  if (isError) {
    return <HomeOverviewLoadingErrorAlert />;
  }

  return (
    <div className="HomeOverview flex flex-col md:py-comfy-lg md:px-comfy-xs gap-comfy-sm rounded-md">
      {variant === HomeOverviewVariant.List && <>{items.map(renderListItem)}</>}
      {variant === HomeOverviewVariant.Gallery && (
        <HomeOverviewGallery
          items={items}
          showMoreTile={showMoreTile}
          moreHref={moreHref}
        />
      )}
      <HomeOverviewCtas
        showCtas={showCtas}
        primaryCtas={primaryCtas}
        secondaryCtas={secondaryCtas}
      />
    </div>
  );
};
